import {Application, Container, Emitter, Graphics, IPixiAppService, Text} from "../../services/pixi-app/pixi-app.service.interface";
import {PixiSpineResource} from "../../services/pixi-app/resources/pixi-spine-resource";
import {IMagic8BallService} from "../../services/magic-8-ball/magic-8-ball.service.interface";
import {IGsapService} from "../../services/gsap/gsap.service.interface";
import {IThemeService} from "../../services/theme/theme.service.interface";
import {ITranslationService} from "../../services/translation/translation.service.interface";
import {IMediaQueryService} from "../../services/media-query/media-queries.interface";
import {IInGameTicketService} from "../../services/in-game-ticket/in-game-ticket.service.interface";
import {PixiAsset} from "../../services/pixi-app/pixi-assets-manager/asset/pixi-asset";
import {IPixiLibWrapper} from "../../services/pixi-app/pixi-lib-wrapper/pixi-lib-wrapper.interface";
import {AddDisposeActionFn} from "../../services/pixi-app/pixi-animation.interface";
import {IInGameTicketPrize} from "../../services/in-game-ticket/prize/abstract/in-game-ticket-prize-types";
import {DESIGN_DIMENSIONS} from "./magic-8-ball-consts";
import {Check} from "@sparkware/app-utils";
import {IInGameChallengeService} from "../../services/in-game-challenge/in-game-challenge.service.interface";
import {MAGIC8BALL_OPENED_FROM} from "../../services/magic-8-ball/magic-8-ball-opened-from.enum";

export class Magic8BallAnimationRenderer {
    // 'present_prize' is when the prizes are presented before the shake / "mock_prize" is during the shake (when the prizes goes through the center of the ball)
    private prizePresentMode: "present_prize" | "mock_prize" = "present_prize";
    private currentPrizeIndex: number | null = null;
    private onResizeHandler: () => void;
    private objects: {
        bottomPrizesLayer: Container;
        bottomPrizesList: Container[];
        bottomPrizesMask: Graphics;
        ball: PixiSpineResource;
        background: PixiSpineResource;
        buttonShakeTheBallWrapper: Container;
        titleTexts: Container;
        prizePresentationText: Text;
        winTitleTextsContainer: Container;
        backToPlayButtonWrapper: Container;
        shakeAgainButtonWrapper: Container;
        winInfoTextsContainer: Container;
        bgParticlesEmitter: Emitter;
    } = {
        bottomPrizesLayer: null,
        bottomPrizesList: [],
        bottomPrizesMask: null,
        ball: null,
        background: null,
        buttonShakeTheBallWrapper: null,
        titleTexts: null,
        prizePresentationText: null,
        winTitleTextsContainer: null,
        backToPlayButtonWrapper: null,
        shakeAgainButtonWrapper: null,
        winInfoTextsContainer: null,
        bgParticlesEmitter: null
    };

    private targetWidth = DESIGN_DIMENSIONS.WIDTH;
    private targetHeight = DESIGN_DIMENSIONS.HEIGHT;
    private scaleY = 1;

    private prizeWonIndex: undefined | number;

    constructor(
        private services: {
            pixiApp: IPixiAppService;
            gsap: IGsapService;
            magic8ball: IMagic8BallService;
            theme: IThemeService;
            translation: ITranslationService;
            mediaQuery: IMediaQueryService;
            inGameTicket: IInGameTicketService;
            inGameChallenge: IInGameChallengeService;
        },
        private pixiApp: Application<HTMLCanvasElement>,
        private assets: Record<'backgroundParticles' | 'star' | 'ball' | 'background' | 'blue_layer' | 'button' | 'prize' | 'transparentTextureBg', PixiAsset>,
        private addDisposeAction: AddDisposeActionFn,
        private pixiLibWrapper: IPixiLibWrapper,
        private prizes: undefined | IInGameTicketPrize[],
        private openedFrom: MAGIC8BALL_OPENED_FROM,
        private onTicketClaim: () => Promise<number>,
        private onNextTicketPrepare: () => Promise<IInGameTicketPrize[] | null>,
        private shouldShowBackToPlayButton: () => boolean,
        private onResize: undefined | ((scale: number) => void)
    ) {
        const rootContainer = this.createRootContainer(pixiLibWrapper);
        // this.objects.rootContainer = rootContainer;


        /**
         * Since this animation will be presented full screen there is no need to complicate the resize logic
         * by computing the space inside the container and resizing according to that. we take the whole screen.
         */
        this.onResizeHandler = () => { this.fitToBoundaries(pixiApp, rootContainer); }
        this.onResizeHandler();
        window.addEventListener("resize", this.onResizeHandler);


        const background = this.createAndShowBackground(assets.background, pixiLibWrapper);
        rootContainer.addChild(background.spineAnimation as any);
        this.objects.background = background;

        const blueLayer = this.createAndShowBlueLayer(assets.blue_layer, pixiLibWrapper);
        rootContainer.addChild(blueLayer.sprite as any);
        // this.objects.background = background;

        const ball = this.createAndShowBall(assets.ball, assets.transparentTextureBg, pixiApp, addDisposeAction, pixiLibWrapper);
        rootContainer.addChild(ball.spineAnimation as any);
        this.objects.ball = ball;

        const prizePresentationText = this.createPrizePresentationText(pixiLibWrapper);
        rootContainer.addChild(prizePresentationText as any);
        this.objects.prizePresentationText = prizePresentationText;

        const titleTexts = this.createTitleTexts(pixiLibWrapper);
        rootContainer.addChild(titleTexts as any);
        this.objects.titleTexts = titleTexts;

        const bottomPrizesLayer = this.createBottomPrizesLayer(pixiLibWrapper);
        rootContainer.addChild(bottomPrizesLayer as any);
        this.objects.bottomPrizesLayer = bottomPrizesLayer;

        const bottomPrizesMask = this.createPrizesBottomLayerMask(pixiLibWrapper);
        rootContainer.addChild(bottomPrizesMask as any);
        // but Mircea, why do you need to put this reference in two places?
        // because typings in pixi are shit, if I try to do this: this.objects.bottomPrizesLayer.mask.visible = false; I get a typing error.
        // but this.objects.bottomPrizesMask.visible = false is OK
        this.objects.bottomPrizesMask = bottomPrizesMask;
        this.objects.bottomPrizesLayer.mask = bottomPrizesMask;

        if (this.prizes) {
            const bottomPrizesList = this.createPrizesForBottomLayer(assets.prize, pixiLibWrapper);
            bottomPrizesList.forEach((prizeObject, i) => {
                prizeObject.x = (prizeObject.width * i) + (2 * i);
                bottomPrizesLayer.addChild(prizeObject as any);
            });
            this.objects.bottomPrizesList = bottomPrizesList;
        }

        const buttonShakeTheBallWrapper = this.createShakeTheBallButton(assets.button, assets.prize, addDisposeAction, pixiLibWrapper);
        rootContainer.addChild(buttonShakeTheBallWrapper as any);
        this.objects.buttonShakeTheBallWrapper = buttonShakeTheBallWrapper;

        const {particleContainer, emitter} = this.createBackgroundParticles(assets.backgroundParticles, [assets.star], pixiLibWrapper);
        rootContainer.addChild(particleContainer as any);
        this.objects.bgParticlesEmitter = emitter;

        /**
         * Objects that will be used when the shaking is over and the prize won is revealed
         */
        const winTitleTextsContainer = this.createWinTitleText(pixiLibWrapper);
        rootContainer.addChild(winTitleTextsContainer as any);
        this.objects.winTitleTextsContainer = winTitleTextsContainer;

        const winInfoTextsContainer = this.createWinInfoText(pixiLibWrapper);
        rootContainer.addChild(winInfoTextsContainer as any);
        this.objects.winInfoTextsContainer = winInfoTextsContainer;

        const backToPlayButtonWrapper = this.createBackToPlayButton(assets.button, addDisposeAction, pixiLibWrapper);
        rootContainer.addChild(backToPlayButtonWrapper as any);
        this.objects.backToPlayButtonWrapper = backToPlayButtonWrapper;

        const shakeAgainButtonWrapper = this.createShakeAgainButton(assets.button, addDisposeAction, pixiLibWrapper, assets.prize);
        rootContainer.addChild(shakeAgainButtonWrapper as any);
        this.objects.shakeAgainButtonWrapper = shakeAgainButtonWrapper;

        pixiApp.stage.addChild(rootContainer as any);

        this.start(pixiLibWrapper);
    }

    dispose() {
        window.removeEventListener("resize", this.onResizeHandler);
        // THERE IS A PROBLEM HERE BECAUSE DISPOSING A PIXI SPINE RESOURCE ALSO DISPOSES RAW ASSET AND WHEN THE PIXI APP ALSO ATTEMPS TO DESTROY ALL LOADED ASSETS IT THROWS AN ERROR BECAUSE IT IS TRYING TWICE
        this.objects.background.dispose();
        this.objects.ball.dispose();

        // PRIZE BACKGROUND IS CREATED INSIDE THE BALL CREATE METHOD, FOR NOW WE DON'T HAVE ACCESS
        // prizeBackground.dispose();

        // THE BUTTONS WRAPPERS ARE CONTAINERS THAT KEEP THE SPINE ANIMATIONS FOR BUTTONS. NEED TO FIND A WAY TO DISPOSE THEM
        // buttonShakeTheBallWrapper
        // backToPlayButtonWrapper
    }

    
    /** ///////////////////////////////////////////////////////////////////////////////////////
     ** ////////// ** ROOT CONTAINER ** ///////////////////////////////////////////////////////
     */ ///////////////////////////////////////////////////////////////////////////////////////
    private createRootContainer(pixiLibWrapper: IPixiLibWrapper) {
        const rootContainer = pixiLibWrapper.createContainer();
        rootContainer.name = "root";
        rootContainer.sortableChildren = true;
        return rootContainer;
    }


    /** ///////////////////////////////////////////////////////////////////////////////////////
     ** ////////// ** BACKGROUND ** ///////////////////////////////////////////////////////////
     */ ///////////////////////////////////////////////////////////////////////////////////////
    private createAndShowBackground(pixiAsset: PixiAsset, pixiLibWrapper: IPixiLibWrapper) {
        const background = pixiLibWrapper.createPixiSpineResource(pixiAsset);
    
        background.spineAnimation.x = -27;
        background.spineAnimation.y = -54;
        background.spineAnimation.zIndex = 10;
        background.spineAnimation.name = "background";
        background.spineAnimation.state.setAnimation(0, "static", false);
        return background;
    }


    /** ///////////////////////////////////////////////////////////////////////////////////////
     ** ////////// ** BLUE LAYER WITH A CIRCLE THAT SITS BETWEEN //////////////////////////////
     ** ////////// THE BACKGROUND+STARPARTICLES AND THE BALL ** ///////////////////////////////
     */ ///////////////////////////////////////////////////////////////////////////////////////
     private createAndShowBlueLayer(pixiAsset: PixiAsset, pixiLibWrapper: IPixiLibWrapper) {
        const bluelayer = pixiLibWrapper.createPixiSpriteResource(pixiAsset);
        bluelayer.sprite.x = -266;
        bluelayer.sprite.y = -50;
        bluelayer.sprite.scale.x = 0.5;
        bluelayer.sprite.scale.y = 0.5;
        bluelayer.sprite.zIndex = 15;
        bluelayer.sprite.name = "bluelayer";
        return bluelayer;
    }


    /** ///////////////////////////////////////////////////////////////////////////////////////
     ** ////////// ** BAll ** /////////////////////////////////////////////////////////////////
     */ ///////////////////////////////////////////////////////////////////////////////////////
    private createAndShowBall(ballPixiAsset: PixiAsset, transparentTextureBgPixiAsset: PixiAsset, pixiApp: Application<HTMLCanvasElement>, addDisposeAction: AddDisposeActionFn, pixiLibWrapper: IPixiLibWrapper) {
        const ball = pixiLibWrapper.createPixiSpineResource(ballPixiAsset);
        ball.spineAnimation.x = 360;
        ball.spineAnimation.y = 360;
        ball.spineAnimation.name = "ball";
        ball.spineAnimation.zIndex = 20;

        ball.spineAnimation.state.addListener({
            event: (entry, event) => {
                switch(event.data.name) {
                    // "mock_prize" event occurs every time a possible or the actual prize passed the center of the ball and it's not visible any more
                    // it gives us the opportunity to present another prize for when the container will pass the center of the ball again
                    case 'mock_prize': {
                        let prize: string;
                        // 'present_prize' is when the prizes are presented before the shake
                        if(this.prizePresentMode === 'present_prize') {
                            prize = this.prizes[this.currentPrizeIndex].prizeString();
                        } else { // "mock_prize" is during the shake (when the prizes goes through the center of the ball)
                            prize = this.prizes[Math.floor(Math.random() * this.prizes.length)].prizeString();
                        }
    
                        const container = pixiLibWrapper.createContainer();
                        const prizeBackground = pixiLibWrapper.createPixiSpriteResource(transparentTextureBgPixiAsset);
                        const prizeText = pixiLibWrapper.createText(prize.split(" ").join("\n"), { 
                            fontFamily: '888-Orbit',
                            fontWeight: '900',
                            align: 'center',
                            fontSize: 25,
                            fill: 0xffcf00,
                            lineHeight: 26
                        });
    
                        prizeText.x = prizeBackground.sprite.width / 2 - prizeText.getLocalBounds().width / 2;
                        prizeText.y = prizeBackground.sprite.height / 2 - prizeText.getLocalBounds().height / 2;
    
                        container.addChild(prizeBackground.sprite as any);
                        container.addChild(prizeText as any);
    
                        const winTextRenderTexture = pixiApp.renderer.generateTexture(container);
    
                        ball.spineAnimation.hackTextureBySlotName("amount", winTextRenderTexture, { width: container.width, height: container.height } as any);

                        // last but not least, add a disposable action so that prizeBackground will be cleaned after disposing the animation
                        addDisposeAction(() => { prizeBackground.dispose(); });
                        break;
                    }
                    // "prize" event occurs the actual prize will be presented to the user.
                    case 'prize': {
                        let prize = this.prizes[this.prizeWonIndex];
                        const container = pixiLibWrapper.createContainer();
                        const prizeBackground = pixiLibWrapper.createPixiSpriteResource(transparentTextureBgPixiAsset);
                        const prizeText = pixiLibWrapper.createText(prize.prizeString().split(" ").join("\n"), { 
                            fontFamily: '888-Orbit',
                            fontWeight: '900',
                            align: 'center',
                            fontSize: 25,
                            fill: 0xffcf00,
                            lineHeight: 26
                        });
    
                        prizeText.x = prizeBackground.sprite.width / 2 - prizeText.getLocalBounds().width / 2;
                        prizeText.y = prizeBackground.sprite.height / 2 - prizeText.getLocalBounds().height / 2;
    
                        container.addChild(prizeBackground.sprite as any);
                        container.addChild(prizeText as any);
    
                        const winTextRenderTexture = pixiApp.renderer.generateTexture(container);
    
                        ball.spineAnimation.hackTextureBySlotName("amount", winTextRenderTexture, { width: container.width, height: container.height } as any);

                        // last but not least, add a disposable action so that prizeBackground will be cleaned after disposing the animation
                        addDisposeAction(() => { prizeBackground.dispose(); });

                        break;
                    }
                    case 'particle_win': {
                        this.playWinParticles();
                        break;
                    }
                }
            }
        });

        return ball;
    }
    private async playWinParticles() {
        await this.playSpineAnimation(this.objects.background, "animation_particle");
        await this.playSpineAnimation(this.objects.background, "animation_particle");
        await this.playSpineAnimation(this.objects.background, "static");
    }


    /** ///////////////////////////////////////////////////////////////////////////////////////
     ** ////////// ** PRIZE PRESENTATION TEXT (beneath the ball and above the prizes) ** //////
     */ ///////////////////////////////////////////////////////////////////////////////////////
    private createPrizePresentationText(pixiLibWrapper: IPixiLibWrapper) {
        const prizePresentationText = pixiLibWrapper.createText(
            this.services.translation.translate("MAGIC_8_BALL_PRIZES_PRESENTATION_TEXT"),
            { fontFamily: '888-Orbit', fontWeight: '400', fontSize: 16, fill: 0xffffff }
        );
        prizePresentationText.alpha = 0;
        prizePresentationText.y = 680;
        prizePresentationText.zIndex = 20;
        prizePresentationText.name = "prizePresentationText";
        this.centerPixiTextToWidth(prizePresentationText, DESIGN_DIMENSIONS.WIDTH, 0);
        return prizePresentationText;
    }
    private async showPrizePresentationText() {
        this.objects.prizePresentationText.visible = true;
        await this.services.gsap.animate(this.objects.prizePresentationText, {alpha: 1, duration: 0.5});
    }
    private async hidePrizePresentationText() {
        await this.services.gsap.animate(this.objects.prizePresentationText, {alpha: 0, duration: 0.5});
        this.objects.prizePresentationText.visible = false;
    }


    /** ///////////////////////////////////////////////////////////////////////////////////////
     ** ////////// ** TITLE TEXTS (above the ball) ** /////////////////////////////////////////
     */ ///////////////////////////////////////////////////////////////////////////////////////
    private createTitleTexts(pixiLibWrapper: IPixiLibWrapper) {
        const titleText1 = pixiLibWrapper.createText(
            this.services.translation.translate(
                this.services.inGameTicket.hasAvailableTickets ? "MAGIC_8_BALL_TITLE_ROW1" : "MAGIC_8_BALL_UNCOMPLETED_TITLE_ROW1"
            ),
            { fontFamily: '888-Orbit', fontWeight: '900', fontSize: 32, fill: 0xffffff }
        );
        /**
         * After tests on different devices (screen sizes) I got to the conclusion that the text is most probably to take 2 lines if it exceeds 
         * (this.targetWidth / this.scaleY) / 16.5 characters. The 16.5 constant was deducted empiricaly by counting how many characters fit in one row
         * So, if the line exceeds that number of characters, we lower the fontSize a bit
         */
        const wordWrapWidth = this.targetWidth / this.scaleY;
        const titleTextTranslated = this.services.translation.translate(
            this.services.inGameTicket.hasAvailableTickets ? "MAGIC_8_BALL_TITLE_ROW2" : "MAGIC_8_BALL_UNCOMPLETED_TITLE_ROW2"
        );
        const fontSize = (titleTextTranslated.length > (wordWrapWidth/16.5)) ? 28 : 32;
        const titleText2 = pixiLibWrapper.createText(
            titleTextTranslated,
            {
                fontFamily: '888-Orbit', fontWeight: '900', fontSize, fill: 0xffcf00,
                wordWrap: true, wordWrapWidth, align: 'center',
            }
        );
        const titleTextsContainer = pixiLibWrapper.createContainer();
        titleTextsContainer.x = 265;
        titleTextsContainer.y = 90;
        titleTextsContainer.alpha = 0;
        titleTextsContainer.addChild(titleText1 as any);
        titleTextsContainer.addChild(titleText2 as any);

        this.centerPixiTextToWidth(titleText1, DESIGN_DIMENSIONS.WIDTH, titleTextsContainer.x);
        this.centerPixiTextToWidth(titleText2, DESIGN_DIMENSIONS.WIDTH, titleTextsContainer.x);

        titleText2.y = 40;
        titleTextsContainer.zIndex = 21;

        return titleTextsContainer;
    }
    private async showTitleTexts() {
        this.objects.titleTexts.visible = true;
        await this.services.gsap.animate(this.objects.titleTexts, {alpha: 1, duration: 0.5});
    }
    private async hideTitleTexts() {
        await this.services.gsap.animate(this.objects.titleTexts, {alpha: 0, duration: 0.70});
        this.objects.titleTexts.visible = false;
    }


    /** ///////////////////////////////////////////////////////////////////////////////////////
     ** ////////// ** BOTTOM PRIZES LAYER MASK ** //////////////////////////////////////////////
     ** ////////// ** (when they are presented they appear sliding down like appearing from behind something) 
     */ ///////////////////////////////////////////////////////////////////////////////////////
    private createPrizesBottomLayerMask(pixiLibWrapper: IPixiLibWrapper) {
        const prizesMask = pixiLibWrapper.createGraphics();
        prizesMask.beginFill(0xff0000);
        prizesMask.drawRect(0, 0, 740, 100);
        prizesMask.name = "prizesMask";
        prizesMask.y = 715;
        return prizesMask;
    }
    
    /** ///////////////////////////////////////////////////////////////////////////////////////
     ** ////////// ** INDIVIDUAL PRIZE FOR BOTTOM LAYER ** ////////////////////////////////////
     */ ///////////////////////////////////////////////////////////////////////////////////////
    private createPrize(text: string, prizeAsset: PixiAsset, pixiLibWrapper: IPixiLibWrapper) {
        const prizeLayer = pixiLibWrapper.createContainer();
        const prizeBackground = pixiLibWrapper.createPixiSpriteResource(prizeAsset);

        prizeBackground.sprite.scale = {
            x: 0.2,
            y: 0.2
        }
        prizeLayer.addChild(prizeBackground.sprite as any);

        const prizeText = pixiLibWrapper.createText(
            text.split(" ").join("\n"),
            { fontFamily: '888-Orbit', fontWeight: '900', align: 'center', fontSize: 14, fill: 0xffcf00, lineHeight: 15 }
        );

        prizeText.x = prizeBackground.sprite.width / 2 - prizeText.getLocalBounds().width / 2;
        prizeText.y = prizeBackground.sprite.height / 2 - prizeText.getLocalBounds().height / 2;

        prizeLayer.addChild(prizeText as any);

        return prizeLayer;
    }
    private createPrizesForBottomLayer(prizeAsset: PixiAsset, pixiLibWrapper: IPixiLibWrapper) {
        const prizeObjects = this.prizes.map(prize => this.createPrize(prize.prizeString(), prizeAsset, pixiLibWrapper));
        return prizeObjects;
    }
    private resetPrizesInBottomLayer() {
        this.objects.bottomPrizesLayer.removeChildren();
        this.objects.bottomPrizesList = [];
    }

    /** ///////////////////////////////////////////////////////////////////////////////////////
     ** ////////// ** BOTTOM PRIZES LAYER ** //////////////////////////////////////////////////
     ** ////////// ** (a Container that keeps prizes created with createPrize) ////////////////
     */ ///////////////////////////////////////////////////////////////////////////////////////
    private createBottomPrizesLayer(pixiLibWrapper: IPixiLibWrapper) {
        const prizeLayer = pixiLibWrapper.createContainer();
        prizeLayer.name = "prizeLayer";
        prizeLayer.zIndex = 20;
        prizeLayer.x = 146;
        prizeLayer.y = 612;

        return prizeLayer;
    }
    private async hideBottomPrizesLayer() {
        await this.services.gsap.animate(this.objects.bottomPrizesLayer, {alpha: 0, duration: 0.5});
        this.objects.bottomPrizesLayer.visible = false;
        this.objects.bottomPrizesMask.visible = false;
    }
    private async showBottomPrizesLayer() {
        this.objects.bottomPrizesLayer.visible = true;
        this.objects.bottomPrizesMask.visible = true;
        await this.services.gsap.animate(this.objects.bottomPrizesLayer, {alpha: 1, duration: 0});
    }

    /** ///////////////////////////////////////////////////////////////////////////////////////
     ** ////////// ** CTA BUTTON ** ///////////////////////////////////////////////////////////
     */ ///////////////////////////////////////////////////////////////////////////////////////
    private createButton(
        buttonName: string, buttonLabel: string, onClick: () => void,
        clickCBTriggerEventType: 'pointerdown' | 'pointerup',
        buttonAsset: PixiAsset, addDisposeAction: AddDisposeActionFn, pixiLibWrapper: IPixiLibWrapper
    ) {
        const buttonWrapper = pixiLibWrapper.createContainer();
        buttonWrapper.name = buttonName + "Wrapper";

        const button = pixiLibWrapper.createPixiSpineResource(buttonAsset);
        button.spineAnimation.name = buttonName + "Spine";

        const buttonText = pixiLibWrapper.createText(
            buttonLabel,
            { fontFamily: '888-Orbit', fontWeight: '900', fontSize: 18, fill: 0xffcf00, lineHeight: 20 }
        );
        buttonText.name = buttonName + "Text";

        buttonWrapper.addChild(button.spineAnimation as any);
        buttonWrapper.addChild(buttonText as any);

        buttonWrapper.x = 360;
        buttonWrapper.y = 550;
        // center the text
        this.centerPixiTextToWidth(buttonText, DESIGN_DIMENSIONS.WIDTH, buttonWrapper.x);
        // buttonText.x = -70;
        buttonText.y = -11;

        buttonWrapper.eventMode = "static";
        buttonWrapper.cursor = "pointer";

        buttonWrapper.on(clickCBTriggerEventType, onClick);

        buttonWrapper.zIndex = 100;
        buttonWrapper.alpha = 0;
        buttonWrapper.visible = false;

        // TODO: Check if it's better this line to be in showButton instead
        button.spineAnimation.state.setAnimation(0, "idle", true);
        
        // last but not least, add a disposable action so that button will be cleaned after disposing the animation
        addDisposeAction(() => { button.dispose(); });

        return buttonWrapper;
    }


    /** ///////////////////////////////////////////////////////////////////////////////////////
     ** ////////// ** SHAKE THE BALL BUTTON ** ////////////////////////////////////////////////
     */ ///////////////////////////////////////////////////////////////////////////////////////
    private createShakeTheBallButton(buttonAsset: PixiAsset, prizeAsset: PixiAsset, addDisposeAction: AddDisposeActionFn, pixiLibWrapper: IPixiLibWrapper) {
        return this.createButton(
            "shakeTheBall",
            this.services.translation.translate("MAGIC_8_BALL_SHAKE_THE_BALL_BUTTON"),
            () => {
                this.startShakin(prizeAsset, pixiLibWrapper)
            },
            'pointerdown',
            buttonAsset, addDisposeAction, pixiLibWrapper
        );
    }
    private async showShakeTheBallButton() {
        this.objects.buttonShakeTheBallWrapper.visible = true;
        await this.services.gsap.animate(this.objects.buttonShakeTheBallWrapper, {alpha: 1, duration: 1});
    }
    private async hideShakeTheBallButton() {
        // this.objects.buttonShakeTheBallWrapper.off("pointerdown", this.startShakin);
        await this.services.gsap.animate(this.objects.buttonShakeTheBallWrapper, {alpha: 0, duration: 0.75});
        this.objects.buttonShakeTheBallWrapper.visible = false;
    }


    /** ///////////////////////////////////////////////////////////////////////////////////////
     ** ////////// ** BACKGROUND PARTICLES (the stars that look slike snow) ** ////////////////
     */ ///////////////////////////////////////////////////////////////////////////////////////
    private createBackgroundParticles(backgroundParticlesAsset: PixiAsset, assets: PixiAsset[], pixiLibWrapper: IPixiLibWrapper) {
        const particleContainer = pixiLibWrapper.createParticleContainer();
        particleContainer.setProperties({
            scale: true,
            position: true,
            rotation: true,
            uvs: true,
            alpha: true,
        });
        particleContainer.zIndex = 11;
        const rawAssets = assets.map((a) => a.rawAsset);
        const newConfig = pixiLibWrapper.particleContainerUpgradeConfig(backgroundParticlesAsset.rawAsset, rawAssets);
        const emitter = pixiLibWrapper.createEmitter(particleContainer as any, newConfig);

        return {particleContainer, emitter};
    }
    private showBackgroundParticles() {
        this.objects.bgParticlesEmitter.playOnceAndDestroy(() => {
            // console.log("createBackgroundParticles")
        });
        return Promise.resolve();
    }

    /** ///////////////////////////////////////////////////////////////////////////////////////
     ** ////////// ** WIN TITLE TEXT (above the ball) ** //////////////////////////////////////
     */ ///////////////////////////////////////////////////////////////////////////////////////
    private createWinTitleText(pixiLibWrapper: IPixiLibWrapper) {
        const winTitleTextsContainer = pixiLibWrapper.createContainer();
        
        winTitleTextsContainer.x = 265;
        winTitleTextsContainer.y = 90;
        winTitleTextsContainer.alpha = 0;
        winTitleTextsContainer.zIndex = 21;

        return winTitleTextsContainer;
    }
    private updateWinTitleText(pixiLibWrapper: IPixiLibWrapper) {
        this.objects.winTitleTextsContainer.removeChildren();
        if (Check.isNullOrUndefined(this.prizeWonIndex)) {
            return;
        }

        const winTitleText1 = pixiLibWrapper.createText(
            this.services.translation.translate("MAGIC_8_BALL_WIN_TITLE_ROW1"),
            { fontFamily: '888-Orbit', fontWeight: '900', fontSize: 32, fill: 0xffffff }
        );
        
        /**
         * After tests on different devices (screen sizes) I got to the conclusion that the text is most probably to take 2 lines if it exceeds 
         * (this.targetWidth / this.scaleY) / 16.5 characters. The 16.5 constant was deducted empiricaly by counting how many characters fit in one row
         * So, if the line exceeds that number of characters, we lower the fontSize a bit
         */
        const wordWrapWidth = this.targetWidth / this.scaleY;
        const localizedWinText = this.services.translation.translate("MAGIC_8_BALL_WIN_TITLE_ROW2", {prizeWon: this.prizes[this.prizeWonIndex].prizeString()});
        const fontSize = (localizedWinText.length > (wordWrapWidth/16.5)) ? 28 : 32;
        const winTitleText2 = pixiLibWrapper.createText(
            localizedWinText,
            {
                fontFamily: '888-Orbit', fontWeight: '900', fontSize, fill: 0xffcf00,
                wordWrap: true, wordWrapWidth, align: 'center',
            }
        );

        this.objects.winTitleTextsContainer.addChild(winTitleText1 as any);
        this.objects.winTitleTextsContainer.addChild(winTitleText2 as any);
        this.centerPixiTextToWidth(winTitleText1, DESIGN_DIMENSIONS.WIDTH, this.objects.winTitleTextsContainer.x);
        this.centerPixiTextToWidth(winTitleText2, DESIGN_DIMENSIONS.WIDTH, this.objects.winTitleTextsContainer.x);
        // winTitleText1.x = 20;
        winTitleText2.y = 40;
    }
    private async showWinTitleText() {
        this.objects.winTitleTextsContainer.visible = true;
        await this.services.gsap.animate(this.objects.winTitleTextsContainer, {alpha: 1, duration: 0.5});
    }
    private async hideWinTitleTexts() {
        await this.services.gsap.animate(this.objects.winTitleTextsContainer, {alpha: 0, duration: 0.70});
        this.objects.winTitleTextsContainer.visible = false;
    }

    /** ///////////////////////////////////////////////////////////////////////////////////////
     ** ////////// ** WIN INFO TEXT (beneath the ball and above the prizes) ** ////////////////
     */ ///////////////////////////////////////////////////////////////////////////////////////
    private createWinInfoText(pixiLibWrapper: IPixiLibWrapper) {
        const winInfoTextsContainer = pixiLibWrapper.createContainer();

        winInfoTextsContainer.y = 670;
        winInfoTextsContainer.alpha = 0;
        winInfoTextsContainer.zIndex = 21;

        return winInfoTextsContainer;
    }
    private updateWinInfoText(pixiLibWrapper: IPixiLibWrapper) {
        this.objects.winInfoTextsContainer.removeChildren();
        if (Check.isNullOrUndefined(this.prizeWonIndex)) {
            return;
        }

        const localizedWinText = this.services.translation.translate("MAGIC_8_BALL_WIN_INFO_TEXT_ROW1", {prizeWon: this.prizes[this.prizeWonIndex].prizeString()});
        const winInfoText1 = pixiLibWrapper.createText(
            localizedWinText,
            {
                fontFamily: '888-Orbit', fontWeight: '900', fontSize: 16, fill: 0xffcf00,
                wordWrap: true, wordWrapWidth: this.targetWidth / this.scaleY, align: 'center',
            }
        );
        // const winInfoText2 = pixiLibWrapper.createText(
        //     this.services.translation.translate("keep playing to get more\nrounds with shake-8"), 
        //     { fontFamily: '888-Orbit', fontWeight: '900', fontSize: 16, fill: 0xffffff, align: 'center' }
        // );
        this.objects.winInfoTextsContainer.addChild(winInfoText1 as any);
        // this.objects.winInfoTextsContainer.addChild(winInfoText2 as any);
        this.centerPixiTextToWidth(winInfoText1, DESIGN_DIMENSIONS.WIDTH, this.objects.winInfoTextsContainer.x);
        // this.centerPixiTextToWidth(winInfoText2, DESIGN_DIMENSIONS.WIDTH, this.objects.winInfoTextsContainer.x);
        // winInfoText2.y = 30;
    }
    private async showWinInfoText() {
        this.objects.winInfoTextsContainer.visible = true;
        await this.services.gsap.animate(this.objects.winInfoTextsContainer, {alpha: 1, duration: 0.5});
    }
    private async hideWinInfoTexts() {
        await this.services.gsap.animate(this.objects.winInfoTextsContainer, {alpha: 0, duration: 0.70});
        this.objects.winInfoTextsContainer.visible = false;
    }

    _getBackButtonText() {
        switch (this.openedFrom) {
            case MAGIC8BALL_OPENED_FROM.GAME:
                return this.services.translation.translate("MAGIC_8_BALL_BACK_TO_GAME_BUTTON");
                
            case MAGIC8BALL_OPENED_FROM.MENU:
            default:
                return this.services.translation.translate("BACK");
        }
    }

    /** ///////////////////////////////////////////////////////////////////////////////////////
     ** ////////// ** BACK TO PLAY BUTTON ** //////////////////////////////////////////////////
     */ ///////////////////////////////////////////////////////////////////////////////////////
    private createBackToPlayButton(buttonAsset: PixiAsset, addDisposeAction: AddDisposeActionFn, pixiLibWrapper: IPixiLibWrapper) {
        const label = this._getBackButtonText();
        
        return this.createButton(
            "backToPlay",
            label.toUpperCase(),
            () => {          
                setTimeout(() => {  
                    // Mobile issue: Rapid overlay dismissal causes accidental control center activation.
                    // Delay gives time for overlay DOM removal, preventing event bubbling to background elements.
                    this.services.magic8ball.hideMagicBall();
                    this.services.inGameChallenge.sendAnalytics('Click', label);
                }, 50);
            },
            'pointerup',
            buttonAsset, addDisposeAction, pixiLibWrapper
        );
    }
    private async showBackToPlayButton() {
        this.objects.backToPlayButtonWrapper.visible = true;
        await this.services.gsap.animate(this.objects.backToPlayButtonWrapper, {alpha: 1, duration: 1});
    }
    // private async hideBackToPlayButton() {
    //     this.objects.backToPlayButtonWrapper.off("pointerdown", this.startShakin);
    //     await this.services.gsap.animate(this.objects.backToPlayButtonWrapper, {alpha: 0, duration: 0.75});
    //     this.objects.backToPlayButtonWrapper.visible = false;
    // }


    /** ///////////////////////////////////////////////////////////////////////////////////////
     ** ////////// ** SHAKE AGAIN BUTTON ** //////////////////////////////////////////////////
     */ ///////////////////////////////////////////////////////////////////////////////////////
    private createShakeAgainButton(buttonAsset: PixiAsset, addDisposeAction: AddDisposeActionFn, pixiLibWrapper: IPixiLibWrapper, prizeAsset: PixiAsset) {
        return this.createButton(
            "shakeAgain",
            this.services.translation.translate("MAGIC_8_BALL_SHAKE_AGAIN_BUTTON"),
            () => {
                this.startShakeAgain(prizeAsset, pixiLibWrapper)
            },
            'pointerdown',
            buttonAsset, addDisposeAction, pixiLibWrapper
        );
    }
    private async showShakeAgainButton() {
        this.objects.shakeAgainButtonWrapper.visible = true;
        await this.services.gsap.animate(this.objects.shakeAgainButtonWrapper, {alpha: 1, duration: 1});
    }
    private async hideShakeAgainButton() {
        // this.objects.shakeAgainButtonWrapper.off("pointerdown", this.startShakin);
        await this.services.gsap.animate(this.objects.shakeAgainButtonWrapper, {alpha: 0, duration: 0.75});
        this.objects.shakeAgainButtonWrapper.visible = false;
    }



    /** ///////////////////////////////////////////////////////////////////////////////////////
     ** ////////// ** In this section there are 2 utilitary methods used for comodity ** //////
     ** ////////// ** centerPixiTextToWidth, playSpineAnimation ** ////////////////////////////
     */ ///////////////////////////////////////////////////////////////////////////////////////
    private centerPixiTextToWidth(pixiText: Text, width: number, offset: number = 0) {
        const localBounds = pixiText.getLocalBounds();
        const textWidth = localBounds.width;
        pixiText.x = (width / 2 - textWidth / 2) - offset;
    }

    /**
     * Promifies the spine animation
     */
    private playSpineAnimation(pixiSpineResource: PixiSpineResource, animationName: string, loop = false): Promise<void> {
        return new Promise((resolve, reject) => {
            try {
                const completeListener = {
                    complete: () => {
                        pixiSpineResource.spineAnimation.state.removeListener(completeListener);
                        resolve();
                    }
                };

                pixiSpineResource.spineAnimation.state.addListener(completeListener)

                pixiSpineResource.spineAnimation.state.setAnimation(0, animationName, loop);
            } catch (e) {
                reject(e);
            }
        });
    };

    /** ///////////////////////////////////////////////////////////////////////////////////////
     ** ////////// ** adaptSize is the handler for resizing theanimation ** ///////////////////
     */ ///////////////////////////////////////////////////////////////////////////////////////
    private fitToBoundaries(pixiApp: Application<HTMLCanvasElement>, rootContainer: Container) {
        const designRatio = DESIGN_DIMENSIONS.WIDTH / DESIGN_DIMENSIONS.HEIGHT;

        if (this.services.mediaQuery.lAndAbove) {
            // width is larger than 1024 and we can render the normal PC
            // at this size, the device most probably is a desktop or a tablet (doesnt matter if in landscape or in portrait) 
            const viewportWidth = window.innerWidth * 0.9;
            const viewportHeight = window.innerHeight * 0.9;

            const viewportRatio = viewportWidth / viewportHeight;
            if (viewportRatio > designRatio) {
                // the viewport is wider so the height must be taken as maximum and the width must be computed to respect the design ratio
                this.targetHeight = viewportHeight;
                this.targetWidth = this.targetHeight * designRatio;
            } else {
                // the viewport is narrower so the width must be taken as maximum and the height must be computed to respect the design ratio
                this.targetWidth = viewportWidth;
                this.targetHeight = this.targetWidth / designRatio;
            }
        } else if (this.services.mediaQuery.portrait) {
            const sait = parseInt(window.getComputedStyle(document.documentElement).getPropertyValue("--safeAreaInsetTop").replace("px", ""));
            const sair = parseInt(window.getComputedStyle(document.documentElement).getPropertyValue("--safeAreaInsetRight").replace("px", ""));
            const saib = parseInt(window.getComputedStyle(document.documentElement).getPropertyValue("--safeAreaInsetBottom").replace("px", ""));
            const sail = parseInt(window.getComputedStyle(document.documentElement).getPropertyValue("--safeAreaInsetLeft").replace("px", ""));
            // small devices like tablets in portrait mode and phones in portrait mode
            // we can render the animation fullscreen in mobile mode 
            const viewportWidth = window.innerWidth - sair - sail;
            const viewportHeight = window.innerHeight - sait - saib;

            this.targetWidth = viewportWidth;
            this.targetHeight = viewportHeight;
        } else {
            // small devices like tablets or phones in landscape mode
            // TODO: Show warning
        }

        // Because mobiles have tall screens, the device ratio (width/height) is smaller than the design ratio.
        // So we scale the animation to fit the screen height, and the width of the animation will be bigger than the screen so we just center it
        // On PC, scaleY and scaleX have the same value because the animation is shown to scale so no need to compute both
        // let scaleX = targetWidth / DESIGN_DIMENSIONS.WIDTH;
        this.scaleY = this.targetHeight / DESIGN_DIMENSIONS.HEIGHT;

        // The usage of window.devicePixelRatio in the next few lines is just a gimmick, without it the animation would look a bit fuzzy/pixelated on high density display
        // Using this trick the animation looks much better. But from a functionality point of view there is no difference in using devicePixelRatio or not
        pixiApp.renderer.resize(this.targetWidth * window.devicePixelRatio, this.targetHeight * window.devicePixelRatio);

        const canvas = pixiApp.view;
        canvas.style.width = (pixiApp.renderer.width / window.devicePixelRatio) + "px";
        canvas.style.height = (pixiApp.renderer.height / window.devicePixelRatio) + "px";

        // this formula will return 0 always on PC because the canvas animation will respect the design ratio
        rootContainer.x = ((this.targetWidth - DESIGN_DIMENSIONS.WIDTH * this.scaleY) / 2) * window.devicePixelRatio;
        rootContainer.scale = {
            x: this.scaleY * window.devicePixelRatio,
            y: this.scaleY * window.devicePixelRatio
        };

        if (this.onResize) {
            this.onResize(this.scaleY);
        }
    }

    
    private async presentPrize(remainingPrizesToPresent: IInGameTicketPrize[], pixiLibWrapper: IPixiLibWrapper) {
        if (remainingPrizesToPresent && remainingPrizesToPresent.length > 0) {
            this.currentPrizeIndex = this.prizes.length - remainingPrizesToPresent.length;
            remainingPrizesToPresent.shift();
            const currentPrizeObject = this.objects.bottomPrizesList[this.currentPrizeIndex];

            this.services.gsap.animate(currentPrizeObject, {y: 100, duration: 0.5});
            await this.playSpineAnimation(this.objects.ball, "present_prize");
            await this.presentPrize(remainingPrizesToPresent, pixiLibWrapper);
        } else {
            await this.playSpineAnimation(this.objects.ball, "out_intro");
        }
    }

    private async start(pixiLibWrapper: IPixiLibWrapper) {
        this.prizePresentMode = "present_prize";
        await this.playSpineAnimation(this.objects.ball, "static");
        // after, show the background particles
        this.showBackgroundParticles();
        // first show the ball and play the animation with the number 8 filling up
        await this.playSpineAnimation(this.objects.ball, "intro");
        // after, present the texts "Shake the Magic 8 Ball" "and win one of the rewards:"
        this.showTitleTexts();
        if (this.prizes) {
            await this.showPrizePresentationText();
            // after, present the prizes one by one both in the center of the ball and down
            const prizesToPresent = [...this.prizes]// structuredClone(this.prizes);
            await this.presentPrize(prizesToPresent, pixiLibWrapper);
        } else {
            await this.playSpineAnimation(this.objects.ball, "out_intro");
        }
        // after, show shake the ball button only if there is a prize to be won. otherwise, the animation was open just to see the prizes and show back to play
        if (this.shouldShowBackToPlayButton()) {
            await this.showBackToPlayButton();
        } else {
            await this.showShakeTheBallButton();
        }
    }

    private async startShakeAgain(prizeAsset: PixiAsset, pixiLibWrapper: IPixiLibWrapper) {
        this.prizeWonIndex = undefined;

        // hide results of the previous shake
        this.hideWinTitleTexts();
        this.hideWinInfoTexts();
        this.hideShakeAgainButton();

        this.prizePresentMode = "present_prize";
        await this.playSpineAnimation(this.objects.ball, "static");
        // after, show the background particles
        this.showBackgroundParticles();
        // show the ball and play the animation with the number 8 filling up
        // but this time don't await, we'll do other stuff in the meantime
        await this.playSpineAnimation(this.objects.ball, "out_intro");

        this.startShakin(prizeAsset, pixiLibWrapper);
    }

    private startShakin = async (prizeAsset: PixiAsset, pixiLibWrapper: IPixiLibWrapper) => {
        this.onTicketClaim().then(actualPrizeIndex => {
            this.prizeWonIndex = actualPrizeIndex;
            
            // update the prize won texts that will be shown after shake
            this.updateWinTitleText(pixiLibWrapper);
            this.updateWinInfoText(pixiLibWrapper);
        });

        this.prizePresentMode = "mock_prize";
        this.hideTitleTexts();
        await this.hideShakeTheBallButton();
        await this.playSpineAnimation(this.objects.ball, "start");
        while (Check.isNullOrUndefined(this.prizeWonIndex)) {
            await this.playSpineAnimation(this.objects.ball, "loop");
        }
        await this.playSpineAnimation(this.objects.ball, "win");
        // we hide the bottom prizes layer even if we will show it again moments later. This way we hint the player that the prizes were updated
        await this.hideBottomPrizesLayer();
        this.hidePrizePresentationText();
        this.showWinTitleText();
        this.showWinInfoText();

        
        const nextTicketPrizes = await this.onNextTicketPrepare();
        if (nextTicketPrizes === null) {
            await this.showBackToPlayButton();
        } else {
            this.prizes = nextTicketPrizes;
            // reset the available prizes in the bottom layer ...
            this.resetPrizesInBottomLayer();
            // ... and add the new available prizes
            const bottomPrizesList = this.createPrizesForBottomLayer(prizeAsset, pixiLibWrapper);
            bottomPrizesList.forEach((prizeObject, i) => {
                prizeObject.x = (prizeObject.width * i) + (2 * i);
                this.objects.bottomPrizesLayer.addChild(prizeObject as any);
            });
            this.objects.bottomPrizesList = bottomPrizesList;
            // we can show the bottom layer because the mask will keep it from being visible
            await this.showBottomPrizesLayer();
            // instead of presenting prizes one by one we show them all at once to shorten the animation.
            // Also, we don't await the animation, we'll do other stuff in parallel / in the meantime
            this.objects.bottomPrizesList.forEach(prize => {
                this.services.gsap.animate(prize, {y: 100, duration: 0.5});
            });
            await this.showShakeAgainButton();
        }
    }
}